import {
  Mesh,
  DoubleSide,
  VideoTexture,
  ShaderMaterial,
  MeshBasicMaterial,
  LinearFilter,
  ClampToEdgeWrapping
} from 'three';

import gsap, { RoughEase, Linear } from 'gsap/all';
import { SCENE_INTERACTION } from '../../constants/scene';
import { NEW_WEBRTC_EVENTS, WEBRTC_EVENTS } from '../../constants/webrtc';
import { SCENE_EVENTS } from '../MVC/Model';
import { videoTextureShaderProps } from './constants/shader';

import { VRProfileHelper } from './VRProfileHelper';
import { animateCamera, waitForElement } from './helpers/worldHelpers';

import {
  chairCircleGeometry,
  chairPlaneGeometry,
  textureLoader,
  defaultTexture,
  materialNegative,
  materialPrimary
} from './helpers/texturesAndGeometries';
import { GLOBAL_EVENTS } from '../../constants/global';

import { WebRtc } from '../WebRTC/_WebRtc';
import { SOCKET_EVENTS } from '../../socket/Socket';
import { ANT_MEDIA_SERVER_BAGELSS_EVENTS, STREAM_NAMES, states } from '../WebRTC/constants';
import NoisyCircleMesh from './NoisyCircleMesh';
import { normalizeBetween, resizedValue } from '../MVC/dataHelpers';
import CircularPlanes from './tour/CircularPlanes';
import { ONE_ON_ONE_UI } from '../Controllers/constants';
import AudioAnalyser3D from './AudioAnalyser3D';

class VRChair extends VRProfileHelper {

  constructor(containerName, camera, texture, x, y, z, model) {
    super(containerName, camera, texture, model);

    this.state = {
      occupied: false,
      me: false,
      videoOn: false,
      avatarImage: null,
      webcamStreamId: null,
    };

    this.iSitOnIt = false;
    this.emptySeat = true;

    this.limits = {
      min: 115,
      max: 570,
      distance: 420
    };
    this.isVideoStarted = false;

    this.isVideoTexture = false;
    this.videoElement = null;
    this.videoTexture = null;

    this.requestAudioLevels = false;
    this.averageVolume = 0;
    this.requestFakeAnimation = true;
    this.videoElementFromSelector = null;
    this.userVideoElementFromSelector = null;

    this.videoMaterial = new MeshBasicMaterial({
      side: DoubleSide,
      transparent: true,
      depthWrite: true,
      depthTest: true,
    });

    this.emptySeatTexture = texture;

    this.mesh = new Mesh(this.model.editMode ? chairPlaneGeometry : chairCircleGeometry, this.material);
    this.mesh.renderOrder = 1;
    this.mesh.type = 'guestSeat';
    this.mesh.name = 'guestSeat';
    this.name = 'guestSeat';

    this.isPublic = this.model.publicStage;

    // Instantiate AudioAnalyser3D
    this.audioAnalyser = AudioAnalyser3D({ mainContainer: this.mesh, width: 10 });
    this.audioAnalyser.container.position.y = -40;


    if (!this.model.editMode) {
      // this.noisy = new NoisyCircleMesh(40, 512);
      // this.newMoodMesh = this.noisy.getMesh();
      // this.planeAudioVisualiser = new CircularPlanes(48, 2, 2, 25, this.mesh, 100);
      // this.planeAudioVisualiser2 = new CircularPlanes(48, 4, 2, 75, this.mesh, 100, Math.PI);
      // this.visualPlanes = this.planeAudioVisualiser.planes;
      // this.mesh.add(this.newMoodMesh);
      // this.newMoodMesh.position.set(0, 0, -30);
    }

    this.add(this.mesh);

    this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

    this.mesh.groupDetails = { title: $trans('chair'), icon: 'chair', editTitle: $trans('edit-chair'), dataLang: 'edit-chair' };

    if (!this.model.editMode) {
      this.addBorder();
      this.addMood();
      this.addMicIcon();
      this.addCameraIcon();
      this.addBusyOnCall();
      // const _y = Math.random() * 0.0006;
      // gsap.to(this.mesh.position, {
      //   y: y + 5,
      //   duration: 2,
      //   repeat: -1,
      //   yoyo: true,
      //   ease: "power1.inOut"
      // });
    }

    const self = this;

    screen.orientation && screen.orientation.addEventListener('change', this.fixRaportRatio);

    // Camera/avatar handlers
    this.handleUserWantsCamera = () => {
      const videoElement = document.querySelector('video#myVideo2');
      this.audioLevelsAnalyzer(videoElement.srcObject);
      this.videoElement = videoElement;
      this.userVideoElementFromSelector = videoElement;
      this.startVideo(videoElement);
      this.takeSeat(true);
      this.fixRaportRatio();
    };

    this.handleUserWantsAvatar = () => {
      this.setAvatarImage(this.model.config.userAvatarUrl);
      this.takeSeat(true);
    };

    this.handleParticipiansAvatar = () => {
      this.stopAudioLevelsAnalyzer();
      this.state.avatarImage && this.setAvatarImage(this.state.avatarImage);
    };

    this.handleMultimediaIcons = () => {
      const { me, occupied, videoOn, userMicOn } = this.state;
      if (me && occupied) {
        if (this.model.iStream.audio) {
          this.micMesh.material = this.micMaterial;
          this.micMoodMesh.material = materialPrimary;
          this.audioAnalyser && this.audioAnalyser.setMuted(false);
        } else {
          this.micMesh.material = this.crossMicMaterial;
          this.micMoodMesh.material = materialNegative;
          this.audioAnalyser && this.audioAnalyser.setMuted(true);
        }
        if (videoOn) {
          this.cameraMesh.material = this.cameraMaterial;
          this.cameraMoodMesh.material = materialPrimary;
        } else {
          this.cameraMesh.material = this.crossCameraMaterial;
          this.cameraMoodMesh.material = materialNegative;
        }
      } else if (occupied) {
        if (videoOn) {
          this.cameraMesh.material = this.cameraMaterial;
          this.cameraMoodMesh.material = materialPrimary;
        } else {
          this.cameraMesh.material = this.crossCameraMaterial;
          this.cameraMoodMesh.material = materialNegative;
        }
        if (userMicOn) {
          this.micMesh.material = this.micMaterial;
          this.micMoodMesh.material = materialPrimary;
          this.audioAnalyser && this.audioAnalyser.setMuted(false);
        } else {
          this.micMesh.material = this.crossMicMaterial;
          this.micMoodMesh.material = materialNegative;
          this.audioAnalyser && this.audioAnalyser.setMuted(true);
        }
      }
    };

    this.occupedWithoutStream = () => {
      const state = this.getState();
      if (state.occupied) {
        if (state.me) {
          if (state.videoOn) {
            this.handleUserWantsCamera();
          } else {
            this.handleUserWantsAvatar();
          }
        } else {
          if (state.videoOn) {
            this.startVideoStream(state.webcamStreamId);
          } else {
            this.setAvatarImage(state.avatarImage);
          }
        }
      } else {
        this.standUp();
      }
    };

    this.isSeatOccupiedByUser = () => {
      if (this.model.editMode) return;
      const occupiedBy = this._resolvedOccupantId();
      this.iSitOnIt = this._sameId(occupiedBy, this.model.config.userId);
      if (this.model.iSitOnThisVRChair === this && occupiedBy && !this.iSitOnIt) {
        this.model.iSitOnThisVRChair = null;
      }
      if (!occupiedBy) {
        this.standUp();
      } else {
        const users = this.model.socket.getUsersObject() || {};
        const userData = users[occupiedBy] || (this.iSitOnIt ? {
          userId: this.model.config.userId,
          userAvatarUrl: this.model.config.userAvatarUrl,
          userVideoOn: this.model.userWantsCamera,
          userMicOn: this.model.userWantsAudio,
        } : null);
        if (userData) {
          this.occupySeat(userData);
        }
      }
      return occupiedBy;
    };

    // Event listeners
    this.model.addEventListener(SCENE_INTERACTION.SCENE_INITIALISED, async (e) => {
      this.occupedWithoutStream();
    });

    this.model.addEventListener(SCENE_INTERACTION.USER_MOOD_CHANGED, (e) => {
      this.state = this.getState();
      if (this.state.me) {
        this.showOrUpdateMood(e.data.mood);
      }
      if (this.alreadyLoadedTexture) {
        this.mesh.material = this.alreadyLoadedTexture;
        return;
      }
    });

    this.model.addEventListener(GLOBAL_EVENTS.MOOD_CHANGED, (e) => {
      const { senderId, data } = e.data;
      this.state = this.getState();

      if (this.state.occupied && String(this.state.userId) === String(senderId)) {
        this.showOrUpdateMood(data.newMood);
      }
      if (this.alreadyLoadedTexture) {
        this.mesh.material = this.alreadyLoadedTexture;
        return;
      }
    });

    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.LEAVE_ROOM_SUCCESS, async (e) => {
      this.occupedWithoutStream();
    });

    this.model.addEventListener(WEBRTC_EVENTS.SCREEN_SHARE_ONLY, async (e) => { });
    this.model.addEventListener(WEBRTC_EVENTS.STREAMING_BOTH, async (e) => { });
    this.model.addEventListener(WEBRTC_EVENTS.CAMERA_ONLY, async (e) => { });
    this.model.addEventListener(ONE_ON_ONE_UI.UI_CHANGE, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(SCENE_EVENTS.USER_INVERTS_CAMERA, async (e) => {
      this.state = this.getState();
      if (!this.state.me) return;
      if (this.model.view.deviceSettingsMenu.selected.userCameraInverted) {
        this.invertCamera();
      } else {
        this.normaliseCamera();
      }
    });

    this.model.addEventListener(SCENE_EVENTS.SOMEONE_SPOKE, (e) => {
      if (this.state.occupied && !this.videoElement) {
        if (this.state.me) {
        } else {
          const { userId } = this.userData;
          if (userId && e.data[userId]) {
            const audioLevels = e.data[userId].audioLevels;
            if (audioLevels) {
              const amplitude = parseFloat(`1.${audioLevels / 10}`);
              this.requestFakeAnimation = true;
              this.fakeAudioLevelsAnimation(amplitude);
            }
          } else {
            this.requestFakeAnimation = false;
          }
        }
      }
    });

    this.model.addEventListener("USER_STARTED_SCREEN_SHARE_PUBLISH", (e) => {
      const occupiedBy = this.isSeatOccupiedByUser();
      if (occupiedBy && (occupiedBy === this.model.config.userId)) {
        const video = document.querySelector('video#myVideo2');
        const { id } = video;
        this.handleUserWantsCamera();
      }
    });

    this.model.addEventListener(SOCKET_EVENTS.USER_VIDEO_STATE_CHANGED, (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.STREAM_INFO, (e) => {
      this.fixRaportRatio();
    });

    this.model.addEventListener(SCENE_EVENTS.USER_SEAT_TURN_CAMERA_OFF, (e) => {
      this.showRightMaterial();
      this.state.me && this.animateCameraClick();
    });

    this.model.addEventListener(SCENE_EVENTS.USER_SEAT_TURN_CAMERA_ON, (e) => {
      this.showRightMaterial();
      this.state.me && this.animateCameraClick();
    });

    this.model.addEventListener(SCENE_EVENTS.USER_SEAT_TURN_MIC_OFF, (e) => {
      this.showRightMaterial();
      this.state.me && this.animateMicClick();
    });

    this.model.addEventListener(SCENE_EVENTS.USER_SEAT_TURN_MIC_ON, (e) => {
      this.showRightMaterial();
      this.state.me && this.animateMicClick();
    });

    this.model.addEventListener(SOCKET_EVENTS.USER_MICROPHONE_STATE_CHANGED, (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(SCENE_INTERACTION.USER_TOGGLE_CAMERA_ON_CTA_MENU, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(SCENE_INTERACTION.SET_OCCUPIED_SEATS, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(SCENE_INTERACTION.SET_OCCUPIED_SCREEN_SHARE_SEATS, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(SCENE_INTERACTION.SOCKET_USER_INFO_CHANGED, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(SCENE_INTERACTION.USER_STOP_SCREEN, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener("ctaMenuClickEvents", (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.JOIN_ROOM_SUCESS, async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener("TEST_RE_CREATE_WEB_STREAMS", async (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.VIDEO_CREATED, (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(ANT_MEDIA_SERVER_BAGELSS_EVENTS.PLAY_STARTED, (e) => {
      this.showRightMaterial();
    });

    this.model.addEventListener(GLOBAL_EVENTS.AVATAR_CHANGED, (e) => {
      this.alreadyLoadedTexture = null;
      this.showRightMaterial();
    });

    this.model.addEventListener("REMOVE_STREAM", (e) => {
      this.showRightMaterial();
    });

    this.startVideoStream = () => {
      const state = this.getState();
      const { occupied, me, videoName, videoOn, avatarImage } = state;
      if (occupied) {
        if (me) {
          if (videoOn) {
            this.handleUserWantsCamera();
          } else {
            this.handleUserWantsAvatar();
          }
        } else {
          if (videoOn) {
            if (this.videoElement && !this.videoElement.srcObject) {
              if (this.videoMaterial) {
                this.videoMaterial.map = null;
              }
              if (this.videoTexture) {
                this.videoTexture.dispose();
                this.videoTexture = null;
              }
              this.videoElement = null;
            }
            if (!document.getElementById(videoName) || this.videoElement) {
              if (this.videoElement && this.videoElement.srcObject) {
                this.audioLevelsAnalyzer(this.videoElement.srcObject);
              }
              return;
            } else {
              const videoElement = document.getElementById(`${videoName}`);
              if (videoElement && videoElement.hasAttribute('canplaythrough')) {
                this.videoElement = videoElement;
                this.startVideo(videoElement);
                this.fixRaportRatio();
                this.audioLevelsAnalyzer(videoElement.srcObject);
              }
            }
          } else {
            this.setAvatarImage(avatarImage);
          }
        }
      } else {
        this.standUp();
      }
    };

    this.mesh.visible = true;
    return this;
  }

  fixRaportRatio(streamWidth, streamHeight) {
    if (!this.videoMaterial) return;
    if (this.videoElement) {
      // this.videoMaterial.uniforms.ratio.value = this.videoElement.videoWidth / this.videoElement.videoHeight;
      // this.videoMaterial.uniforms.isPortrait.value = this.videoElement.videoWidth < this.videoElement.videoHeight;
    }
    if (this.videoElement !== null && this.videoElement !== undefined) {
      if (streamWidth && streamHeight) {
        // this.videoMaterial.uniforms.ratio.value = streamWidth / streamHeight;
        // this.videoMaterial.uniforms.isPortrait.value = streamWidth < streamHeight;
      }
    }
  }

  forcedHCRaportRatio(w, h) {
    // this.videoMaterial.uniforms.ratio.value = w / h;
    // this.videoMaterial.uniforms.isPortrait.value = w < h;
  }

  setMyMood() {
    // const mood = document.querySelector("#user_mood").classList[0].replace("mood_", "");
    // this.showOrUpdateMood(mood);
  }

  updateUserVideoState(state) {
    if (this.videoElement !== null && this.videoElement !== undefined) {
      this.mesh.material = state ? this.videoMaterial : this.material;
      state ? $(this.videoElement).parent().removeClass('hidden') : $(this.videoElement).parent().addClass('hidden');
    }
  }

  useAvatarMaterial = (texture) => {
    // this.mesh.material = this.material;
    // this.material.map = texture;
    // this.isVideoTexture = false;
    // this.setMyMood();
  };

  useVideoMaterial() {
    // if (this.startVideo(document.getElementById('myVideo'))) {
    //   this.isVideoTexture = true;
    // } else {
    //   this.model.alert('error', $trans('error-turn-on-camera'));
    // }
    // this.setMyMood();
  }

  takeSeat() {
    $(`.indicator-${this.mesh.id}`).addClass('taken');
    this.mesh.visible = true;
    if (this.mesh.material && !Array.isArray(this.mesh.material)) {
      this.mesh.material.opacity = 1;
    }
    this.requestAudioLevels = true;
    this.moodMesh.visible = true;
    if (this.model.iSitOnThisVRChair === this) {
      this.state = {
        ...(this.state || {}),
        userId: this.model.config.userId,
        occupied: true,
        me: true,
        videoOn: this.model.userWantsCamera,
        avatarImage: this.model.config.userAvatarUrl,
      };
    }
    return;
  }

  standUp() {
    if (this.model.editMode) return;
    const alreadyEmpty = this.userData === null;
    if (this.moodMesh) this.moodMesh.visible = false;
    this.mesh.visible = false;
    $(`.indicator-${this.mesh.id}`).removeClass('taken');
    this.requestAudioLevels = false;
    if (this.state) {
      this.state = {
        ...this.state,
        userId: null,
        occupied: false,
        me: false,
        videoOn: false,
      };
    }
    if (alreadyEmpty) return;
    this.hideUserOptions();
    this.userData = null;
    this.stopAudioLevelsAnalyzer();
    clearInterval(this.autoSendVolume);
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(error => {
        console.warn('Error closing previous AudioContext:', error);
      });
    }
    this.autoSendVolume = null;
    this.alreadyLoadedTexture && (this.alreadyLoadedTexture.dispose());
    this.alreadyLoadedTexture = false;
    this.audioAnalyser && this.audioAnalyser.setMuted(false);
    if (this.videoMaterial) {
      this.videoMaterial.map = null;
    }
    if (this.videoTexture) {
      this.videoTexture.dispose();
      this.videoTexture = null;
    }
    this.videoElement = null;
    return;
  }

  occupySeat(userData) {
    if (!this.userData) {
      this.animateFirstSeat();
      // this.planeAudioVisualiser.startAnimation();
    }
    this.userData = userData;
    this.emptySeat = false;
    this.takeSeat();
  }

  setBusyTexture = async (on1on1Call, userId) => {
    // if (on1on1Call) {
    //   $('#remoteVideo_' + userId).addClass('hidden');
    //   $('#remoteVideo_' + userId).addClass('on-1on1-call');
    // } else {
    //   $('#remoteVideo_' + userId).removeClass('hidden');
    //   $('#remoteVideo_' + userId).removeClass('on-1on1-call');
    // }
    // this.busyMesh.visible = on1on1Call;
  };

  setAvatarImage(url, apply = true) {
    if (this.alreadyLoadedTexture) {
      this.mesh.material = this.alreadyLoadedTexture;
      this.mesh.material.opacity = 1;
      return;
    }
    const tex = textureLoader.load(
      url,
      () => {
        this.material.map = tex;
        if (apply) {
          this.mesh.material = this.material;
        }
        this.alreadyLoadedTexture = this.material;
        this.mesh.material.opacity = 1;
      },
      undefined,
      (error) => {
        console.error('Texture loading error:', error);
        this.applyDefaultTexture(apply);
      }
    );
  }

  applyDefaultTexture(apply) {
    this.material.map = defaultTexture;
    if (apply) {
      this.mesh.material = this.material;
    }
  }

  startVideo(videoElement, userVideoOn = true, streamId, streamWidth, streamHeight, callShowMaterial = true) {
    if (!videoElement) return false;
    if (this.videoTexture && this.videoTexture.image === videoElement && this.videoMaterial.map === this.videoTexture) {
      return true;
    }
    if (!this.videoTexture) {
      this.videoTexture = new VideoTexture(videoElement);
      this.videoTexture.minFilter = LinearFilter;
      this.videoTexture.magFilter = LinearFilter;
      this.videoTexture.wrapS = ClampToEdgeWrapping;
      this.videoTexture.wrapT = ClampToEdgeWrapping;
    } else if (this.videoTexture.image !== videoElement) {
      this.videoTexture.image = videoElement;
      this.videoTexture.needsUpdate = true;
    }
    this.videoMaterial.map = this.videoTexture;

    if (userVideoOn) {
      console.log('startVideo!', videoElement.srcObject);
      this.handleMultimediaIcons();
      if (streamWidth && streamHeight && streamWidth != 0 && streamHeight != 0) {
        this.fixRaportRatio(streamWidth, streamHeight);
      } else {
        if (streamHeight) {
          this.fixRaportRatio(streamWidth, streamHeight);
        } else {
          this.fixRaportRatio();
        }
      }
    }
    return true;
  }

  _sameId(a, b) {
    return a != null && b != null && String(a) === String(b);
  }

  // Socket map lags behind a local sit/switch. Trust the chair we just took,
  // and treat the chair we just left as empty until the server agrees.
  _resolvedOccupantId() {
    if (this.model.editMode || !this.dbConfig) return undefined;
    let occupiedSeats = {};
    try {
      occupiedSeats = this.model.socket?.getOccupiedSeats?.() || {};
    } catch (error) {
      occupiedSeats = {};
    }
    const socketUserId = occupiedSeats[this.dbConfig.id];
    const myId = this.model.config.userId;
    const localChair = this.model.iSitOnThisVRChair;

    if (localChair === this) {
      if (socketUserId && !this._sameId(socketUserId, myId)) {
        return socketUserId;
      }
      return myId;
    }

    if (localChair && this._sameId(socketUserId, myId)) {
      return undefined;
    }

    return socketUserId || undefined;
  }

  isEmpty() {
    return !this._resolvedOccupantId();
  }

  isMine() {
    return this._sameId(this._resolvedOccupantId(), this.model.config.userId);
  }

  showUserOptions() {
    return;
    $(".cta-menu").removeClass("is-active");
    if (this.iSitOnIt) {
      $("#own-chair-menu").addClass("is-active");
    } else {
      $("#peer-chair-menu").addClass("is-active");
    }
    $("#peerIsFriend").hide();
    $("#peerAddAsFriend").hide();
    if ((this.model.config.userId === this.userId) || !this.userId) return;
    if (this.userId && !this.model.friendsMap[this.userId]) {
      $("#peerIsFriend").hide();
      $("#peerAddAsFriend").show();
    } else {
      $("#peerIsFriend").show();
      $("#peerAddAsFriend").hide();
    }
  }

  hideUserOptions() {
    super.hideUserOptions();
    $("#own-chair-menu").removeClass("is-active");
    $("#peer-chair-menu").removeClass("is-active");
  }

  showInfo() {
    if (!this.userData) return;
    if (this.userData.isGuest) {
      const guestUserModal = $("#guestUserModal");
      guestUserModal.find(".guest-user-name-wrapper-left").html(`${this.userData.userName}`);
      guestUserModal.find(".guest-user-name-wrapper-right").html(`${this.userData.userName}`);
      guestUserModal.find(".guest-user-location").html(`${this.userData.worldName}/${this.userData.currentSceneName}`);
      const messageBtn = guestUserModal.find(".confirm-cancel");
      messageBtn.html($trans('send-msg-text'));
      const videoBtn = guestUserModal.find(".confirm-ok");
      videoBtn.html($trans('video-call-text'));
      messageBtn.off();
      videoBtn.off();
      const handlePeopleChatWithUserClicked = (event, item) => {
        guestUserModal.removeClass("is-active");
        this.model.socket.dispatchEvent({ type: SOCKET_EVENTS.ADD_ONE_ON_ONE_CHAT, data: this.userData });
      };
      const handlePeopleVideoOneOnOneClick = (event, item) => {
        guestUserModal.removeClass("is-active");
        this.model.dispatchEvent({ type: WEBRTC_EVENTS.WEBRTC_VIDEO_ONE_ON_ONE, data: { item } });
      };
      messageBtn.on("click", handlePeopleChatWithUserClicked);
      videoBtn.on("click", handlePeopleVideoOneOnOneClick);

      if (this.model.isGuest) {
        messageBtn.hide();
        videoBtn.hide();
      }
      guestUserModal.addClass("is-active");
    } else {
      this.model.showUserInfo({ data: { ...this.userData, wpId: this.userData.hasProfile }, eventOrigin: '3Dobject' });
    }
    this.hideUserOptions();
  }

  addAsFriend() {
    this.model.worldFetcher.addAsFriend(this.userId, (response) => {
      this.model.onFriendAddedSuccess(response);
    });
  }

  getState() {
    const _occupiedBy = this.isSeatOccupiedByUser();
    if (!_occupiedBy) {
      this.state =
      {
        userId: null,
        occupied: false,
        me: false,
        videoOn: false,
        avatarImage: null,
        webcamStreamId: null,
        videosInfo: null,
        invertedCamera: false,
        userMicOn: false,
        on1on1Call: false,
        videoName: null,
      };
      return this.state;
    }
    if (this._sameId(_occupiedBy, this.model.config.userId)) {
      this.state = {
        userId: this.model.config.userId,
        occupied: true,
        me: true,
        videoOn: this.model.userWantsCamera,
        avatarImage: this.model.config.userAvatarUrl,
        webcamStreamId: null,
        videosInfo: this.model.videosInfo,
        invertedCamera: !!this.model.view?.deviceSettingsMenu?.selected?.userCameraInverted,
        userMicOn: this.model.userWantsAudio,
        on1on1Call: this.model.activeOneOnOneCall,
        videoName: "video#myVideo2",
      };
    } else {
      const data = this.model.socket.getUsersObject()?.[_occupiedBy];
      if (!data) {
        this.state = {
          userId: _occupiedBy,
          occupied: true,
          me: false,
          videoOn: false,
          avatarImage: null,
          webcamStreamId: null,
          videosInfo: null,
          invertedCamera: false,
          userMicOn: false,
          on1on1Call: false,
          videoName: null,
        };
        return this.state;
      }
      const inRoom = this.model.streamController.isInRoom();
      const { userAvatarUrl, userVideoOn, webcamStreamId, videosInfo, currentUserMood, on1on1Call, userId } = data;
      this.state = {
        userId: userId,
        occupied: true,
        me: false,
        videoOn: inRoom && webcamStreamId && userVideoOn,
        avatarImage: userAvatarUrl,
        webcamStreamId: webcamStreamId,
        videosInfo,
        invertedCamera: false,
        currentUserMood,
        userMicOn: data.userMicOn,
        on1on1Call: on1on1Call,
        videoName: `remoteVideo-${STREAM_NAMES.CAMERA}${userId}`
      };
    }
    return this.state;
  }

  showRightMaterial() {
    try {
      this._applySeatMaterial();
    } catch (error) {
      console.error('VRChair.showRightMaterial', error);
    }
  }

  _applySeatMaterial() {
    this.state = this.getState();
    this.busyMesh.visible = this.state.on1on1Call;
    if (this.state.on1on1Call) {
      this.mesh.add(this.busyMesh);
      this.takeSeat();
      return;
    } else {
      this.mesh.remove(this.busyMesh);
    }
    const inRoom = this.model.streamController.isInRoom();
    this.handleMultimediaIcons();
    if (this.state.occupied) {
      this.takeSeat();
      if (this.state.me) {
        if (this.model.userWantsCamera) {
          this.handleUserWantsCamera();
          if (this.state.invertedCamera) {
            this.invertCamera();
          } else {
            this.normaliseCamera();
          }
        } else {
          this.handleUserWantsAvatar();
        }
        return;
      }
      if (inRoom) {
        if (this.state.videoOn) {
          this.mesh.material = this.videoMaterial;
          this.startVideoStream();
          if (this.state.invertedCamera) {
            this.invertCamera();
          } else {
            this.normaliseCamera();
          }
          this.mesh.material = this.videoMaterial;
        } else {
          this.handleParticipiansAvatar();
        }
      } else {
        this.setAvatarImage(this.state.avatarImage);
      }
    } else {
      this.mesh.visible = false;
      $(`.indicator-${this.mesh.id}`).removeClass('taken');
    }
  }

  audioLevelsAnalyzer = (stream) => {
    if (!stream) return;
    if (stream.getAudioTracks().length === 0) {
      console.log(`No audio found in the stream`);
      return;
    }

    if (this.audioContext) {
      this.audioContext.close().catch(error => {
        console.warn('Error closing previous AudioContext:', error);
      });
    }

    this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioSource = this.audioContext.createMediaStreamSource(stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.4;
    audioSource.connect(this.analyser);
    this.requestAudioLevels = true;
  };

  stopAudioLevelsAnalyzer() {
    this.requestAudioLevels = false;
    this.moodMesh.scale.set(1, 1, 1);
    if (this.audioContext) {
      this.audioContext.close().catch(error => {
        console.warn('Error closing previous AudioContext:', error);
      });
    }
    this.analyser = null;
  }

  fakeAudioLevelsAnimation(amplitude, scaleStrength) {
    if (this.isAnimating) return;
    const dur = 0.2;
    gsap.to(this.moodMesh.scale, {
      duration: dur,
      x: amplitude,
      y: amplitude,
      ease: RoughEase.ease.config({ strength: 8, points: 5, template: Linear.easeNone, randomize: false }),
      onComplete: () => {
        this.isAnimating = false;
        gsap.to(this.moodMesh.scale, {
          duration: 0.25,
          x: 1,
          y: 1
        });
      }
    });
    this.isAnimating = true;
  }

  invertCamera() {
    this.mesh.material = this.videoMaterial;
    const videoTexture = this.mesh.material.map;
    const video = this.videoElement;
    if (!videoTexture || !video) return;

    videoTexture.repeat.set(-1, 1);
    videoTexture.offset.set(1, 0);
    videoTexture.needsUpdate = true;
    gsap.killTweensOf(this.mesh.rotation);
  }

  normaliseCamera() {
    this.mesh.material = this.videoMaterial;
    const videoTexture = this.mesh.material.map;
    const video = this.videoElement;
    if (!videoTexture || !video) return;

    const videoAspectRatio = video.videoWidth / video.videoHeight;
    const geometryAspectRatio = 40 / 40;

    let repeatX, repeatY;
    if (videoAspectRatio > geometryAspectRatio) {
      repeatX = geometryAspectRatio / videoAspectRatio;
      repeatY = 1;
    } else {
      repeatX = 1;
      repeatY = videoAspectRatio / geometryAspectRatio;
    }

    videoTexture.repeat.set(repeatX, repeatY);
    videoTexture.offset.set(0.5 - repeatX / 2, 0.5 - repeatY / 2);
    videoTexture.repeat.set(1, 1);
    videoTexture.offset.set(0, 0);
    gsap.killTweensOf(this.mesh.rotation);
  }

  update(clock) {
    if (!this.requestAudioLevels || !this.analyser) return;
    if (this.audioAnalyser && this.audioAnalyser.isMuted) return;

    const volumes = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(volumes);

    // Calculate average amplitude
    const sum = volumes.reduce((a, b) => a + b, 0);
    const amplitude = sum / volumes.length;

    // Smooth amplitude using exponential moving average
    this._smoothedAmplitude = this._smoothedAmplitude
      ? this._smoothedAmplitude * 0.8 + amplitude * 0.2
      : amplitude;

    // Normalize to scale factor (1 to 10)
    let scale = 1 + (this._smoothedAmplitude / 255) * 9;

    // Check for silence
    const silenceThreshold = 10; // Adjust as needed (0-255 range)
    const isSilence = this._smoothedAmplitude < silenceThreshold;
    if (isSilence) {
      scale = 1; // Static scale when silent
    }

    // Calculate elapsed time and sinusoidal value
    const elapsedTime = clock.getElapsedTime();
    const sinValue = 0.1 * Math.sin(elapsedTime * 2 * Math.PI * 2); // 2 Hz with 0.1 amplitude

    // Initialize scale history for delayScale if not exists (only for mesh1, mesh2)
    this._scaleHistory = this._scaleHistory || [[], []]; // Arrays for mesh1, mesh2
    this._scaleHistory.forEach(history => {
      history.push(scale); // Add current scale
      if (history.length > 2) history.shift(); // Keep only 2 frames (0, 1 delays)
    });

    if (this.audioAnalyser) {
      this.audioAnalyser.update({
        scaleMesh1: isSilence ? scale : (scale / 2) * (1 + sinValue),
        scaleMesh2: isSilence ? scale : (scale / 2) * (1 + sinValue),
        scaleMesh3: scale
      });
    }
  }
}

export { VRChair };
